import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { PersonAvatarWithIndicators, PersonSelectRow } from './PersonAvatarWithIndicators';
import { fetchPersonActivityMaps } from '../utils/personActivityCounts';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  Archive,
  Search,
  MapPin,
  Calendar,
  FileText,
  User,
  ExternalLink,
  Link2,
  BookmarkPlus,
  Loader2,
  AlertCircle,
  Filter,
  X,
  Building,
  ScrollText,
  Heart,
  Plane,
  Shield,
  Home,
  BookOpen,
  Newspaper,
  MoreHorizontal
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Record type icons
const RecordTypeIcon = ({ type }) => {
  const iconClass = "w-4 h-4";
  
  switch (type) {
    case 'census':
      return <Building className={`${iconClass} text-blue-400`} />;
    case 'birth':
      return <Heart className={`${iconClass} text-pink-400`} />;
    case 'death':
      return <ScrollText className={`${iconClass} text-muted-foreground`} />;
    case 'marriage':
      return <Heart className={`${iconClass} text-rose-400`} />;
    case 'immigration':
      return <Plane className={`${iconClass} text-teal-400`} />;
    case 'military':
      return <Shield className={`${iconClass} text-emerald-400`} />;
    case 'land':
      return <Home className={`${iconClass} text-amber-400`} />;
    case 'probate':
      return <FileText className={`${iconClass} text-purple-400`} />;
    case 'church':
      return <BookOpen className={`${iconClass} text-indigo-400`} />;
    case 'newspaper':
      return <Newspaper className={`${iconClass} text-orange-400`} />;
    default:
      return <FileText className={`${iconClass} text-muted-foreground`} />;
  }
};

// Confidence score badge
const ConfidenceScore = ({ score }) => {
  const percentage = Math.round(score * 100);
  let colorClass = 'text-muted-foreground bg-secondary border-border';
  let label = 'Low';
  
  if (percentage >= 80) {
    colorClass = 'text-emerald-400 bg-emerald-900/30 border-emerald-800/50';
    label = 'High';
  } else if (percentage >= 60) {
    colorClass = 'text-amber-400 bg-amber-900/20 border-amber-800/40';
    label = 'Medium';
  }
  
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${colorClass}`}>
      {label} ({percentage}%)
    </span>
  );
};

// Source badge
const SourceBadge = ({ source }) => {
  const sourceLabels = {
    familysearch: 'FamilySearch',
    ancestry: 'Ancestry',
    findmypast: 'FindMyPast',
    myheritage: 'MyHeritage',
    'newspapers.com': 'Newspapers',
    fold3: 'Fold3',
    national_archives: 'National Archives',
    local_archives: 'Local Archives',
    user_uploaded: 'User Upload',
    placeholder: 'Preview Data'
  };
  
  return (
    <span className="text-[10px] px-2 py-0.5 rounded bg-secondary text-muted-foreground border border-border">
      {sourceLabels[source] || source}
    </span>
  );
};

// Single Archive Record Card
const ArchiveRecordCard = ({
  record,
  onView,
  onLink,
  onSave,
  onSaveToJournal,
  onLinkAndJournal,
  allPeople,
  storyCounts = {},
  documentCounts = {},
  isSaving,
  isSavingToJournal,
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleLinkConfirm = async (andJournal = false) => {
    if (!selectedPersonId) {
      toast.error('Please select a person to link this record to');
      return;
    }
    setIsLinking(true);
    try {
      await onLink(record, selectedPersonId);
      if (andJournal && onLinkAndJournal) {
        await onLinkAndJournal(record, selectedPersonId);
      }
      setShowLinkDialog(false);
      setSelectedPersonId('');
    } finally {
      setIsLinking(false);
    }
  };
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="bg-card border-secondary hover:border-border transition-colors">
          <CardContent className="p-3 space-y-2">
            {/* Header with record type and confidence */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <RecordTypeIcon type={record.record_type} />
                <span className="text-xs font-medium text-foreground/90 capitalize">
                  {record.record_type?.replace('_', ' ')} Record
                </span>
              </div>
              <ConfidenceScore score={record.confidence_score} />
            </div>
            
            {/* Primary name */}
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
              <p className="text-sm font-medium text-foreground">{record.primary_name}</p>
            </div>
            
            {/* Date and location */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground/80">
              {record.record_year && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {record.record_year}
                </span>
              )}
              {record.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {record.location}
                </span>
              )}
            </div>
            
            {/* Snippet/preview */}
            {record.snippet && (
              <p className="text-[11px] text-muted-foreground/70 italic leading-relaxed line-clamp-2">
                "{record.snippet}"
              </p>
            )}
            
            {/* Source and citation */}
            <div className="flex flex-wrap items-center gap-1.5">
              <SourceBadge source={record.source} />
              {record.source_name && (
                <span className="text-[9px] text-muted-foreground/70">{record.source_name}</span>
              )}
              {record.record_reference && (
                <span className="text-[9px] text-muted-foreground/50 font-mono" title="Record reference">
                  {record.record_reference}
                </span>
              )}
              {record.reliability && record.reliability !== 'unknown' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-muted/30 text-muted-foreground">
                  {record.reliability === 'primary' ? 'Primary source' : record.reliability === 'secondary' ? 'Secondary' : record.reliability}
                </span>
              )}
              {record.is_placeholder && (
                <span className="text-[9px] text-muted-foreground/50 italic">Preview data</span>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(record)}
                className="flex-1 h-7 text-xs border-border text-foreground hover:bg-secondary"
                data-testid={`view-record-btn-${record.record_id}`}
              >
                <ExternalLink className="w-3 h-3 mr-1.5" />
                View Record
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(true)}
                className="flex-1 h-7 text-xs border-border text-foreground hover:bg-secondary"
                data-testid={`link-record-btn-${record.record_id}`}
              >
                <Link2 className="w-3 h-3 mr-1.5" />
                Link to Person
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSave(record)}
                disabled={isSaving}
                className="h-7 px-2 text-xs border-amber-800/40 text-amber-400/90 hover:bg-amber-900/20"
                data-testid={`save-record-btn-${record.record_id}`}
                title="Save to your archive"
              >
                {isSaving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <BookmarkPlus className="w-3 h-3" />
                )}
              </Button>
              {onSaveToJournal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSaveToJournal(record)}
                  disabled={isSavingToJournal}
                  className="h-7 px-2 text-[10px] border-border text-muted-foreground hover:text-foreground"
                  data-testid={`add-record-to-journal-btn-${record.record_id}`}
                  title="Add to Research Journal"
                >
                  {isSavingToJournal ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <ScrollText className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      {/* Link to Person Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Link Record to Family Member</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Connect this historical record to someone in your family tree.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Record summary */}
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <div className="flex items-center gap-2 mb-2">
                <RecordTypeIcon type={record.record_type} />
                <span className="text-sm font-medium text-foreground capitalize">
                  {record.record_type?.replace('_', ' ')} Record
                </span>
              </div>
              <p className="text-sm text-foreground/90">{record.primary_name}</p>
              <p className="text-xs text-muted-foreground">
                {record.record_year && `${record.record_year}`}
                {record.location && ` • ${record.location}`}
              </p>
            </div>
            
            {/* Person selector */}
            <div className="space-y-2">
              <Label className="text-sm text-foreground/90">Select family member</Label>
              <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                <SelectTrigger className="bg-secondary border-border h-auto min-h-10 py-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {selectedPersonId && (() => {
                      const p = allPeople.find((x) => x.id === selectedPersonId);
                      if (!p) return null;
                      return (
                        <PersonAvatarWithIndicators
                          photoUrl={
                            p.profile_image_url ? resolveAssetUrl(BACKEND_URL, p.profile_image_url) : null
                          }
                          initials={`${p.given_names?.charAt(0) || ''}${p.surname?.charAt(0) || ''}`}
                          size="xs"
                          storyCount={storyCounts[p.id] ?? 0}
                          documentCount={documentCounts[p.id] ?? 0}
                        />
                      );
                    })()}
                    <SelectValue
                      placeholder="Choose a person from your tree..."
                      className="truncate text-left"
                    />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[min(60vh,320px)]">
                  {allPeople.map((person) => (
                    <SelectItem
                      key={person.id}
                      value={person.id}
                      textValue={
                        `${person.given_names || ''} ${person.surname || ''}`.trim() || 'Unknown'
                      }
                      className="py-2 pl-2 pr-8"
                    >
                      <PersonSelectRow
                        person={person}
                        photoBaseUrl={BACKEND_URL}
                        size="sm"
                        storyCount={storyCounts[person.id] ?? 0}
                        documentCount={documentCounts[person.id] ?? 0}
                      />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-[11px] text-muted-foreground/70 italic">
              Linking records helps build a more complete picture of your ancestor's life journey.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLinkDialog(false)} className="border-border">
              Cancel
            </Button>
            {onLinkAndJournal && (
              <Button
                variant="outline"
                onClick={() => handleLinkConfirm(true)}
                disabled={!selectedPersonId || isLinking}
                className="border-border"
              >
                {isLinking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BookmarkPlus className="w-4 h-4 mr-2" />}
                Link and add to Journal
              </Button>
            )}
            <Button
              onClick={() => handleLinkConfirm(false)}
              disabled={!selectedPersonId || isLinking}
              className="bg-amber-700 hover:bg-amber-600"
            >
              {isLinking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Link Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// View Record Dialog
const ViewRecordDialog = ({ record, open, onClose, onLink, allPeople }) => {
  if (!record) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <RecordTypeIcon type={record.record_type} />
            <span className="capitalize">{record.record_type?.replace('_', ' ')} Record</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Historical record details
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Primary info */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground/60 uppercase tracking-wider">Primary Name</p>
                <p className="text-foreground font-medium">{record.primary_name}</p>
              </div>
            </div>
            
            {record.record_year && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground/60 uppercase tracking-wider">Record Year</p>
                  <p className="text-foreground">{record.record_year}</p>
                </div>
              </div>
            )}
            
            {record.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground/60 uppercase tracking-wider">Location</p>
                  <p className="text-foreground">{record.location}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Snippet */}
          {record.snippet && (
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-1">Record Extract</p>
              <p className="text-sm text-foreground/90 italic leading-relaxed">"{record.snippet}"</p>
            </div>
          )}

          {/* Citation */}
          {(record.source_name || record.record_reference || record.reliability) && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Citation</p>
              <div className="p-3 rounded-lg bg-secondary border border-border space-y-2">
                {(record.source_name || record.record_reference) && (
                  <p className="text-sm text-foreground/90">
                    {[record.source_name, record.record_reference].filter(Boolean).join(' • ')}
                  </p>
                )}
                {record.reliability && record.reliability !== 'unknown' && (
                  <span className="inline-block text-[10px] px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground">
                    {record.reliability === 'primary' ? 'Primary source' : record.reliability === 'secondary' ? 'Secondary source' : record.reliability}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Metadata */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <SourceBadge source={record.source} />
              <ConfidenceScore score={record.confidence_score} />
            </div>
            <span className="text-[10px] text-muted-foreground/50">
              ID: {record.record_id?.slice(0, 8)}...
            </span>
          </div>
          
          {record.is_placeholder && (
            <p className="text-[11px] text-center text-muted-foreground/60 italic">
              This is preview data. Actual archive integration coming soon.
            </p>
          )}
        </div>
        
        <DialogFooter className="gap-2">
          {onLink && allPeople?.length > 0 && (
            <Button
              variant="outline"
              onClick={() => { onLink(record); onClose(false); }}
              className="border-border"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Link to Person
            </Button>
          )}
          <Button variant="outline" onClick={() => onClose(false)} className="border-border">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Link Record Dialog (panel-level, for use from View Record)
const LinkRecordDialog = ({
  record,
  open,
  onClose,
  onLink,
  onLinkAndJournal,
  allPeople,
  storyCounts = {},
  documentCounts = {},
}) => {
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleLink = async (andJournal = false) => {
    if (!selectedPersonId || !record) return;
    setIsLinking(true);
    try {
      await onLink(record, selectedPersonId);
      if (andJournal && onLinkAndJournal) {
        await onLinkAndJournal(record, selectedPersonId);
      }
      setSelectedPersonId('');
      onClose();
    } finally {
      setIsLinking(false);
    }
  };

  if (!record) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Link Record to Family Member</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Connect this historical record to someone in your family tree.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="p-3 rounded-lg bg-secondary border border-border">
            <div className="flex items-center gap-2 mb-2">
              <RecordTypeIcon type={record.record_type} />
              <span className="text-sm font-medium text-foreground capitalize">
                {record.record_type?.replace('_', ' ')} Record
              </span>
            </div>
            <p className="text-sm text-foreground/90">{record.primary_name}</p>
            <p className="text-xs text-muted-foreground">
              {record.record_year && `${record.record_year}`}
              {record.location && ` • ${record.location}`}
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-foreground/90">Select family member</Label>
            <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
              <SelectTrigger className="bg-secondary border-border h-auto min-h-10 py-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {selectedPersonId && (() => {
                    const p = allPeople.find((x) => x.id === selectedPersonId);
                    if (!p) return null;
                    return (
                      <PersonAvatarWithIndicators
                        photoUrl={
                          p.profile_image_url ? resolveAssetUrl(BACKEND_URL, p.profile_image_url) : null
                        }
                        initials={`${p.given_names?.charAt(0) || ''}${p.surname?.charAt(0) || ''}`}
                        size="xs"
                        storyCount={storyCounts[p.id] ?? 0}
                        documentCount={documentCounts[p.id] ?? 0}
                      />
                    );
                  })()}
                  <SelectValue
                    placeholder="Choose a person from your tree..."
                    className="truncate text-left"
                  />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-[min(60vh,320px)]">
                {allPeople.map((person) => (
                  <SelectItem
                    key={person.id}
                    value={person.id}
                    textValue={
                      `${person.given_names || ''} ${person.surname || ''}`.trim() || 'Unknown'
                    }
                    className="py-2 pl-2 pr-8"
                  >
                    <PersonSelectRow
                      person={person}
                      photoBaseUrl={BACKEND_URL}
                      size="sm"
                      storyCount={storyCounts[person.id] ?? 0}
                      documentCount={documentCounts[person.id] ?? 0}
                    />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground/70 italic">
            Linking records helps build a more complete picture of your ancestor's life journey.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-border">Cancel</Button>
          {onLinkAndJournal && (
            <Button
              variant="outline"
              onClick={() => handleLink(true)}
              disabled={!selectedPersonId || isLinking}
              className="border-border"
            >
              {isLinking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BookmarkPlus className="w-4 h-4 mr-2" />}
              Link and add to Journal
            </Button>
          )}
          <Button
            onClick={() => handleLink(false)}
            disabled={!selectedPersonId || isLinking}
            className="bg-amber-700 hover:bg-amber-600"
          >
            {isLinking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
            Link Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Archive Search Panel
const ArchiveSearchPanel = ({ treeId, treeName, allPeople = [], onRecordLinked }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingToJournalId, setSavingToJournalId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [linkingRecord, setLinkingRecord] = useState(null);
  const [personStoryCounts, setPersonStoryCounts] = useState({});
  const [personDocumentCounts, setPersonDocumentCounts] = useState({});

  const archivePeopleIdsKey = useMemo(
    () => allPeople.map((p) => p.id).filter(Boolean).sort().join(','),
    [allPeople]
  );

  useEffect(() => {
    if (!treeId || !allPeople.length) {
      setPersonStoryCounts({});
      setPersonDocumentCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { storyCounts, documentCounts } = await fetchPersonActivityMaps(
          BACKEND_URL,
          treeId,
          allPeople
        );
        if (!cancelled) {
          setPersonStoryCounts(storyCounts);
          setPersonDocumentCounts(documentCounts);
        }
      } catch {
        if (!cancelled) {
          setPersonStoryCounts({});
          setPersonDocumentCounts({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allPeople membership covered by archivePeopleIdsKey
  }, [treeId, archivePeopleIdsKey]);

  const handleSaveToJournal = async (record, linkedPersonId) => {
    if (!treeId || !treeName) return;
    setSavingToJournalId(record.record_id);
    try {
      const title = `${record.primary_name || 'Archive record'}${record.record_year ? ` (${record.record_year})` : ''}`;
      await axios.post(`${API}/investigations`, {
        tree_id: treeId,
        tree_name: treeName,
        insight_id: `archive_${record.record_id}`,
        insight_type: 'archive_record',
        insight_title: title,
        insight_description: record.snippet || `${record.record_type} record from ${record.source || 'archive'}`,
        search_params: {
          record_id: record.record_id,
          source: record.source,
          record_type: record.record_type,
          primary_name: record.primary_name,
          record_year: record.record_year,
          location: record.location,
          source_name: record.source_name,
          record_reference: record.record_reference,
          reliability: record.reliability
        },
        linked_individual_ids: linkedPersonId ? [linkedPersonId] : [],
        notes: null
      });
      toast.success('Archive record added to Research Journal');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already saved')) {
        toast.info('This record is already in your Research Journal');
      } else {
        toast.error(err.response?.data?.detail || 'Could not add to journal');
      }
    } finally {
      setSavingToJournalId(null);
    }
  };
  
  // Search state
  const [surname, setSurname] = useState('');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [location, setLocation] = useState('');
  const [recordType, setRecordType] = useState('');
  
  // Results
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const [groupBySource, setGroupBySource] = useState(false);

  const sourceLabels = {
    familysearch: 'FamilySearch',
    ancestry: 'Ancestry',
    national_archives: 'National Archives',
    'newspapers.com': 'Newspapers.com',
    placeholder: 'Demo Results'
  };

  const groupedResults = groupBySource && results.length > 0
    ? results.reduce((acc, r) => {
        const key = r.source_name || sourceLabels[r.source] || r.source || 'Other';
        if (!acc[key]) acc[key] = [];
        acc[key].push(r);
        return acc;
      }, {})
    : null;
  
  // Record types for filter
  const recordTypes = [
    { value: 'all', label: 'All Record Types' },
    { value: 'census', label: 'Census Records' },
    { value: 'birth', label: 'Birth Records' },
    { value: 'death', label: 'Death Records' },
    { value: 'marriage', label: 'Marriage Records' },
    { value: 'immigration', label: 'Immigration Records' },
    { value: 'military', label: 'Military Records' },
    { value: 'land', label: 'Land Records' },
    { value: 'probate', label: 'Probate Records' },
    { value: 'church', label: 'Church Records' },
    { value: 'newspaper', label: 'Newspaper Articles' }
  ];
  
  const handleSearch = async () => {
    if (!surname.trim()) {
      toast.error('Please enter a surname to begin your search');
      return;
    }
    
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const params = new URLSearchParams();
      params.append('surname', surname.trim());
      if (yearStart) params.append('year_start', yearStart);
      if (yearEnd) params.append('year_end', yearEnd);
      if (location.trim()) params.append('location', location.trim());
      if (recordType && recordType !== 'all') params.append('record_type', recordType);
      
      const response = await axios.get(`${API}/archive/search?${params.toString()}`);
      
      setResults(response.data.results || []);
      setTotalResults(response.data.total_results || 0);
      setSearchMessage(response.data.message || '');
      
      if (response.data.results?.length > 0) {
        toast.success(`Found ${response.data.total_results} potential records`, { duration: 3000 });
      } else {
        toast.info('No records found matching your search criteria', { duration: 3000 });
      }
    } catch (error) {
      console.error('Archive search error:', error);
      toast.error('Unable to search archives at this time');
      setResults([]);
      setTotalResults(0);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleClearSearch = () => {
    setSurname('');
    setYearStart('');
    setYearEnd('');
    setLocation('');
    setRecordType('');
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setSearchMessage('');
  };
  
  const handleViewRecord = (record) => {
    setViewingRecord(record);
  };
  
  const handleLinkAndJournal = async (record, personId) => {
    await handleLinkRecord(record, personId);
    await handleSaveToJournal(record, personId);
  };

  const handleLinkRecord = async (record, personId) => {
    try {
      const formData = new FormData();
      formData.append('record_type', record.record_type);
      formData.append('primary_name', record.primary_name);
      if (record.record_year) formData.append('record_date', `${record.record_year}-01-01`);
      if (record.location) formData.append('location', record.location);
      formData.append('person_id', personId);
      if (treeId) formData.append('tree_id', treeId);
      if (record.snippet) formData.append('transcription', record.snippet);
      formData.append('source', record.source || 'user_uploaded');
      if (record.source_citation) formData.append('source_citation', record.source_citation);
      if (record.record_reference) formData.append('record_reference', record.record_reference);
      if (record.source_url) formData.append('source_url', record.source_url);
      if (record.source_name) formData.append('source_name', record.source_name);
      if (record.confidence_score != null) formData.append('confidence_score', String(record.confidence_score));

      await axios.post(`${API}/archive/records`, formData);

      toast.success('Record linked to family member successfully', { duration: 3000 });

      if (onRecordLinked) {
        onRecordLinked(record, personId);
      }
    } catch (error) {
      console.error('Link record error:', error);
      toast.error('Unable to link record at this time');
    }
  };
  
  const handleSaveRecord = async (record) => {
    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append('record_type', record.record_type);
      formData.append('primary_name', record.primary_name);
      if (record.record_year) formData.append('record_date', `${record.record_year}-01-01`);
      if (record.location) formData.append('location', record.location);
      if (treeId) formData.append('tree_id', treeId);
      if (record.snippet) formData.append('transcription', record.snippet);
      formData.append('source', record.source || 'user_uploaded');
      if (record.source_citation) formData.append('source_citation', record.source_citation);
      if (record.record_reference) formData.append('record_reference', record.record_reference);
      if (record.source_url) formData.append('source_url', record.source_url);
      if (record.source_name) formData.append('source_name', record.source_name);
      if (record.confidence_score != null) formData.append('confidence_score', String(record.confidence_score));

      await axios.post(`${API}/archive/records`, formData);

      toast.success('Record saved to your archive', { duration: 3000 });
    } catch (error) {
      console.error('Save record error:', error);
      toast.error('Unable to save record at this time');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      <Card className="bg-gradient-to-b from-card to-card border-secondary backdrop-blur-sm" data-testid="archive-search-panel">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="p-3 pb-2">
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-900/30">
                    <Archive className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-foreground/90">Archive Search</h3>
                    <p className="text-[10px] text-muted-foreground/60">Search historical records</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {totalResults > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/30 text-amber-400/90 border border-amber-800/40">
                      {totalResults} found
                    </span>
                  )}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          
          <CollapsibleContent>
            <CardContent className="p-3 pt-0 space-y-3">
              {/* Search form */}
              <div className="space-y-3">
                {/* Surname - always visible */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground/80">Surname</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
                    <Input
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      placeholder="Enter family surname..."
                      className="pl-8 h-8 text-sm bg-secondary border-border"
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      data-testid="archive-surname-input"
                    />
                  </div>
                </div>
                
                {/* Toggle filters */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Filter className="w-3 h-3 mr-1.5" />
                  {showFilters ? 'Hide Filters' : 'Show More Filters'}
                  {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
                
                {/* Additional filters */}
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-3 overflow-hidden"
                    >
                      {/* Year range */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground/80">Year From</Label>
                          <Input
                            type="number"
                            value={yearStart}
                            onChange={(e) => setYearStart(e.target.value)}
                            placeholder="1800"
                            min="1500"
                            max="2025"
                            className="h-8 text-sm bg-secondary border-border"
                            data-testid="archive-year-start-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground/80">Year To</Label>
                          <Input
                            type="number"
                            value={yearEnd}
                            onChange={(e) => setYearEnd(e.target.value)}
                            placeholder="1950"
                            min="1500"
                            max="2025"
                            className="h-8 text-sm bg-secondary border-border"
                            data-testid="archive-year-end-input"
                          />
                        </div>
                      </div>
                      
                      {/* Location */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground/80">Location</Label>
                        <div className="relative">
                          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
                          <Input
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="City, State, or Country..."
                            className="pl-8 h-8 text-sm bg-secondary border-border"
                            data-testid="archive-location-input"
                          />
                        </div>
                      </div>
                      
                      {/* Record type */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground/80">Record Type</Label>
                        <Select value={recordType} onValueChange={setRecordType}>
                          <SelectTrigger className="h-8 text-sm bg-secondary border-border" data-testid="archive-record-type-select">
                            <SelectValue placeholder="All Record Types" />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {recordTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <span className="flex items-center gap-2">
                                  {type.value !== 'all' && <RecordTypeIcon type={type.value} />}
                                  {type.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Search buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={isSearching || !surname.trim()}
                    className="flex-1 h-8 text-xs bg-amber-700 hover:bg-amber-600"
                    data-testid="archive-search-btn"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-3.5 h-3.5 mr-1.5" />
                        Search Archives
                      </>
                    )}
                  </Button>
                  {hasSearched && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearSearch}
                      className="h-8 px-2 border-border"
                      data-testid="archive-clear-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Results */}
              {hasSearched && (
                <div className="space-y-2 pt-2 border-t border-secondary">
                  {isSearching ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-amber-500 animate-spin mb-2" />
                      <p className="text-xs text-muted-foreground">Searching historical archives...</p>
                    </div>
                  ) : results.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground/80">
                          Found <span className="text-foreground font-medium">{totalResults}</span> potential records
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setGroupBySource(!groupBySource)}
                          className="h-6 text-[10px] text-muted-foreground hover:text-foreground"
                        >
                          {groupBySource ? 'Ungroup' : 'Group by source'}
                        </Button>
                      </div>
                      
                      {searchMessage && (
                        <p className="text-[10px] text-muted-foreground/60 italic">{searchMessage}</p>
                      )}
                      
                      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        <AnimatePresence>
                          {groupedResults ? (
                            Object.entries(groupedResults).map(([sourceName, records]) => (
                              <div key={sourceName} className="space-y-2">
                                <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider sticky top-0 bg-card/95 backdrop-blur py-1">
                                  {sourceName} ({records.length})
                                </p>
                                {records.map((record) => (
                                  <ArchiveRecordCard
                                    key={record.record_id}
                                    record={record}
                                    onView={handleViewRecord}
                                    onLink={handleLinkRecord}
                                    onSave={handleSaveRecord}
                                    onSaveToJournal={treeId && treeName ? handleSaveToJournal : undefined}
                                    onLinkAndJournal={treeId && treeName ? handleLinkAndJournal : undefined}
                                    allPeople={allPeople}
                                    storyCounts={personStoryCounts}
                                    documentCounts={personDocumentCounts}
                                    isSaving={isSaving}
                                    isSavingToJournal={savingToJournalId === record.record_id}
                                  />
                                ))}
                              </div>
                            ))
                          ) : (
                            results.map((record) => (
                            <ArchiveRecordCard
                              key={record.record_id}
                              record={record}
                              onView={handleViewRecord}
                              onLink={handleLinkRecord}
                              onSave={handleSaveRecord}
                              onSaveToJournal={treeId && treeName ? handleSaveToJournal : undefined}
                              onLinkAndJournal={treeId && treeName ? handleLinkAndJournal : undefined}
                              allPeople={allPeople}
                              storyCounts={personStoryCounts}
                              documentCounts={personDocumentCounts}
                              isSaving={isSaving}
                              isSavingToJournal={savingToJournalId === record.record_id}
                            />
                          ))
                          )}
                        </AnimatePresence>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6">
                      <AlertCircle className="w-8 h-8 text-muted-foreground/40 mb-2" />
                      <p className="text-xs text-muted-foreground text-center">
                        No records found matching your search criteria.
                      </p>
                      <p className="text-[10px] text-muted-foreground/60 text-center mt-1">
                        Try broadening your search or adjusting the year range.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Initial state - no search yet */}
              {!hasSearched && (
                <div className="text-center py-4">
                  <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
                    Begin your historical research journey by searching for your ancestors' records.
                    Census, birth, marriage, and more await discovery.
                  </p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
      
      {/* View Record Dialog */}
      <ViewRecordDialog
        record={viewingRecord}
        open={!!viewingRecord}
        onClose={() => setViewingRecord(null)}
        onLink={(rec) => { setLinkingRecord(rec); setViewingRecord(null); }}
        allPeople={allPeople}
      />
      {/* Link Record Dialog (from View Record) */}
      <LinkRecordDialog
        record={linkingRecord}
        open={!!linkingRecord}
        onClose={() => setLinkingRecord(null)}
        onLink={handleLinkRecord}
        onLinkAndJournal={treeId && treeName ? handleLinkAndJournal : undefined}
        allPeople={allPeople}
        storyCounts={personStoryCounts}
        documentCounts={personDocumentCounts}
      />
    </>
  );
};

export default ArchiveSearchPanel;
